import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form.tsx';
import { Button } from '@/components/ui/button.tsx';
import { LoadingSpinner } from '@/components/ui/loading-spinner.tsx';
import React, { useEffect, useRef, useState } from 'react';
import { RectangleEllipsis } from 'lucide-react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router';
import { useAuthStore } from '@/services/store/use-auth-store.tsx';
import { usePostChallenge2FAMutation } from '@/features/auth/hooks/mutations/use-post-challenge-2fa.ts';
import { usePostVerify2FAMutation } from '@/features/auth/hooks/mutations/use-post-verify-2fa.ts';
import { StatusAlert } from '@/features/auth/components/status-alert.tsx';

const loader = document.querySelector('.loader-container')!;
const showLoader = () => {
  loader.classList.remove('loader--hide');
};

const twoFactorVerificationSchema = z.object({
  totpCode: z.string(),
});

export type TwoFactorVerificationFormSchema = z.infer<typeof twoFactorVerificationSchema>;

export function TwoFactorVerificationForm() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [statusMessage, setStatusMessage] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  const { setTokenExpiration, setIsAuthenticated, setUserRole, isAuthenticated } =
    useAuthStore();

  const form = useForm({
    resolver: zodResolver(twoFactorVerificationSchema),
    defaultValues: {
      totpCode: '',
    },
  });

  function onSkipForNow() {
    showLoader();
    setIsAuthenticated(true);
    navigate('/', { replace: true });
  }

  const { mutate: postChallenge2FAMutation, isPending: isPendingPostChallenge2FA } =
    usePostChallenge2FAMutation({
      onSuccess: () => {
        setStatusMessage({
          type: 'success',
          message: 'Two-factor authentication has been successfully set up.',
        });

        setTimeout(() => {
          onSkipForNow();
        }, 2000);
      },
      onError: () => {
        setStatusMessage({
          type: 'error',
          message: 'Failed to set up two-factor authentication. Please try again.',
        });
      },
    });

  const { mutate: postVerify2FAMutation, isPending: isPendingPostVerify2FA } =
    usePostVerify2FAMutation({
      onSuccess: ({ refreshToken, role }) => {
        setStatusMessage({
          type: 'success',
          message: 'Verification code confirmed! Welcome back.',
        });

        setTimeout(() => {
          setTokenExpiration(refreshToken.expiresAt);
          setIsAuthenticated(true);
          setUserRole(role);
          navigate('/', { replace: true });
        }, 1000);
      },
      onError: ({ response }) => {
        setStatusMessage({
          type: 'error',
          message: 'Invalid verification code. Please try again.',
        });

        if (response?.data?.redirectTo === 'LOGIN_PAGE') {
          setTimeout(() => {
            navigate('/', { replace: true });
          }, 1000);
        }
      },
    });

  function onSubmit(values: TwoFactorVerificationFormSchema) {
    setStatusMessage(null);

    if (state?.from === '2FA_SETUP_PAGE') {
      return postChallenge2FAMutation({ totpCode: values.totpCode });
    }

    if (state?.from === 'LOGIN_PAGE') {
      return postVerify2FAMutation({ totpCode: values.totpCode });
    }

    navigate('/', { replace: true });
  }

  const isPending = isPendingPostChallenge2FA || isPendingPostVerify2FA;

  if (!['2FA_SETUP_PAGE', 'LOGIN_PAGE'].includes(state?.from)) {
    return (
      <div className="min-h-[390px]">
        <Navigate to="/" replace={true} />
      </div>
    );
  }

  if (isAuthenticated) {
    return (
      <div className="min-h-[390px]">
        <Navigate to="/" replace={true} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md">
      <div className="flex flex-col gap-4">
        {statusMessage && (
          <div className="flex w-full items-center justify-center">
            <StatusAlert
              type={statusMessage.type}
              message={statusMessage.message}
              className="max-w-[300px]"
            />
          </div>
        )}
        <div className="space-y-2 text-center">
          <div className="flex justify-center">
            <RectangleEllipsis size="52" className="text-[#383E42]" />
          </div>
          <h2 className="text-2xl font-semibold tracking-tight text-gray-900">
            Verify Your Identity
          </h2>
          <p className="text-sm text-gray-600">
            Enter the 6-digit code from your authenticator app
          </p>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="totpCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="sr-only">Verification Code</FormLabel>
                  <FormControl>
                    <VerificationCodeInput {...field} disabled={isPending} />
                  </FormControl>
                  <FormMessage className="text-center" />
                </FormItem>
              )}
            />

            <Button
              disabled={isPending}
              type="submit"
              className="bg-primary h-10 w-full font-medium shadow-sm transition-all hover:shadow-md disabled:opacity-50"
            >
              {isPending ? <LoadingSpinner className="h-5 w-5" /> : 'Verify'}
            </Button>
          </form>
        </Form>

        <div className="flex flex-col items-center justify-center gap-1 text-sm">
          <div className="flex items-center justify-center gap-1 text-sm">
            <span className="text-gray-500">Having trouble?</span>
            {state?.from === '2FA_SETUP_PAGE' ? (
              <button
                onClick={() => onSkipForNow()}
                className="text-primary link-underline h-auto cursor-pointer p-0 font-medium"
              >
                Skip for now
              </button>
            ) : (
              <Link
                to="/reset-2fa"
                className="text-primary link-underline h-auto cursor-pointer p-0 font-medium"
                state={{
                  from: '2FA_VERIFY_PAGE',
                }}
              >
                Reset 2FA
              </Link>
            )}
          </div>
          <div className="flex items-center justify-center">
            <a
              href="https://www.time.gov"
              target="_blank"
              className="text-primary link-underline h-auto cursor-pointer p-0 font-medium"
            >
              Verify device clock synchronization
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

interface VerificationCodeInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

function VerificationCodeInput({ value, onChange, disabled }: VerificationCodeInputProps) {
  const [digits, setDigits] = useState<string[]>(Array(6).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (value) {
      const newDigits = value.split('').slice(0, 6);
      setDigits([...newDigits, ...Array(6 - newDigits.length).fill('')]);
    }
  }, [value]);

  const handleChange = (index: number, digit: string) => {
    if (!/^\d*$/.test(digit)) return;

    const newDigits = [...digits];
    newDigits[index] = digit.slice(-1);
    setDigits(newDigits);
    onChange(newDigits.join(''));

    if (digit && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    if (/^\d+$/.test(pastedData)) {
      const newDigits = pastedData.split('');
      setDigits([...newDigits, ...Array(6 - newDigits.length).fill('')]);
      onChange(pastedData);

      // Focus last input or next empty one
      const nextEmptyIndex = newDigits.length < 6 ? newDigits.length : 5;
      inputRefs.current[nextEmptyIndex]?.focus();
    }
  };

  return (
    <div className="flex justify-center gap-2">
      {digits.map((digit, index) => (
        <input
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          pattern="\d*"
          maxLength={1}
          value={digit}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          disabled={disabled}
          className="h-12 w-10 rounded-lg border border-gray-200 bg-white text-center text-xl font-semibold text-gray-900 shadow-sm transition-all placeholder:text-gray-400 hover:border-gray-300 focus:border-gray-900 focus:ring-2 focus:ring-gray-900 focus:ring-offset-2 focus:outline-none disabled:opacity-50 sm:h-14 sm:w-12"
          placeholder="·"
        />
      ))}
    </div>
  );
}
